import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Share2, Info, Activity, X } from 'lucide-react';
import { LiveHeartRate } from './LiveHeartRate';
import { VitalMetrics } from './VitalMetrics';
import { HealthReport } from './HealthReport';
import { ShareReportTile } from './ShareReportTile';
import { useHealthMetrics } from '../../hooks/useHealthMetrics';
import { vitalMetrics } from './constants';
import { useNavigate } from 'react-router-dom';

export function HealthMonitorPage() {
  const [showReport, setShowReport] = useState(false);
  const metrics = useHealthMetrics();
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Activity className="w-6 h-6 text-primary" />
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Vitals</h2>
              <p className="text-sm text-muted-foreground">Track and optimize your vital signs</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowReport(true)}
              className="theme-button px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share Report
            </button>
            <button
              onClick={() => navigate('/home')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Live Heart Rate Card */}
          <div className="theme-card p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
            <div className="relative">
              <LiveHeartRate currentHeartRate={metrics.heartRate.current} />
            </div>
          </div>

          {/* Info Banner */}
          <div className="theme-card p-4 bg-primary/5 border border-primary/10 rounded-lg">
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-primary shrink-0" />
              <div className="text-sm text-muted-foreground">
                <p>These vital signs were recorded during your sleep last night. Negative trends in multiple metrics may indicate an oncoming or ongoing illness.</p>
                <p className="text-primary mt-1">Doctor's consultation recommended if abnormal patterns persist.</p>
              </div>
            </div>
          </div>

          {/* Vital Metrics Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Primary Metrics */}
            {vitalMetrics.slice(0, 4).map((metric, index) => (
              <VitalMetrics key={index} metric={metric} />
            ))}

            {/* Additional Metrics */}
            <VitalMetrics metric={vitalMetrics[4]} />
            <ShareReportTile onClick={() => setShowReport(true)} />
          </div>
        </div>
      </div>

      {/* Health Report Modal */}
      {showReport && (
        <HealthReport 
          metrics={{
            respiratoryRate: 14.2,
            bloodOxygen: 98,
            restingHR: 54,
            hrv: metrics.hrv.current,
            skinTemp: 2.8,
            sleepScore: 92,
            avgHeartRate: 68,
            recovery: 85
          }}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}